/**
 * RoleAccessSheet Component
 *
 * Slide-over panel for managing a user's role and module access, per the
 * Role & Access redesign mockup:
 *   Role → Threads Advanced Tools → In-App Module Access → Admin Module Access → Summary
 *
 * Persistence flows through the existing RBAC API (role assignment,
 * per-user overrides, threads advanced) — only the presentation and the
 * role/lock matrix (lib/roleAccessConfig) are new.
 */
import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '@/components/ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/context/AuthContext';
import { normalizeRole, isSystemAdminRole } from '@/lib/roles';
import { Loader2, Shield, Settings, Zap, Lock, Check, X, Info } from 'lucide-react';
import {
  useRbacRoles,
  useUserRbac,
  useUpdateUserRbac,
  type ModuleAccessLevel,
  type UpdateUserRbacPayload,
} from '@/lib/rbacApi';
import {
  PANEL_ROLE_SLUGS,
  isPanelRoleSlug,
  isAdminRoleSlug,
  ROLE_DESCRIPTIONS,
  ADMIN_MODULES,
  adminModuleDefault,
  THREADS_MODULE_KEY,
  threadsAdvancedDefault,
} from '@/lib/roleAccessConfig';

interface RoleAccessSheetProps {
  userId: number | null;
  userName: string;
  userEmail: string;
  /** Legacy users.role slug from the row — fallback when the user has no RBAC assignment yet. */
  legacyRole?: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

type ModuleOverrideState = Record<string, ModuleAccessLevel | null>;

const LEVEL_OPTIONS: { value: ModuleAccessLevel; label: string }[] = [
  { value: 'none', label: 'No Access' },
  { value: 'read', label: 'Read Access' },
  { value: 'full', label: 'Full Access' },
];

function LevelIcon({ level }: { level: ModuleAccessLevel }) {
  if (level === 'full') return <Check className="h-3 w-3 text-green-500" />;
  if (level === 'read') return <Info className="h-3 w-3 text-blue-500" />;
  return <X className="h-3 w-3 text-red-500" />;
}

export function RoleAccessSheet({
  userId,
  userName,
  userEmail,
  legacyRole,
  open,
  onOpenChange,
  onSuccess,
}: RoleAccessSheetProps) {
  const { toast } = useToast();
  const { user: actor } = useAuth();
  const actorIsSystemAdmin = isSystemAdminRole(actor?.role);

  const { data: rolesData, isLoading: rolesLoading } = useRbacRoles();
  const { data: userRbac, isLoading: userRbacLoading } = useUserRbac(open ? userId : null);
  const updateMutation = useUpdateUserRbac();

  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [threadsAdvanced, setThreadsAdvanced] = useState(false);
  const [moduleOverrides, setModuleOverrides] = useState<ModuleOverrideState>({});
  const [hasChanges, setHasChanges] = useState(false);
  /** Role change awaiting confirmation (member → admin promotions). */
  const [pendingRoleId, setPendingRoleId] = useState<number | null>(null);

  const allRoles = rolesData?.roles ?? [];
  const isLoading = rolesLoading || userRbacLoading;

  // Current (persisted) role of the target — RBAC assignment first, legacy
  // fallback (normalized: the row carries the raw users.role value).
  const legacyRoleSlug = normalizeRole(legacyRole) || null;
  const targetCurrentSlug = userRbac?.primaryRole?.slug ?? legacyRoleSlug ?? null;

  // The five assignable roles, in panel order. Admin actors cannot assign
  // SYSTEM_ADMIN — but the option stays listed (select is disabled anyway)
  // when it is the target's current role, so the field doesn't render blank.
  const panelRoles = useMemo(
    () =>
      PANEL_ROLE_SLUGS.map((slug) => allRoles.find((r) => r.slug === slug)).filter(
        (r): r is NonNullable<typeof r> =>
          !!r &&
          (actorIsSystemAdmin ||
            r.slug !== 'SYSTEM_ADMIN' ||
            targetCurrentSlug === 'SYSTEM_ADMIN'),
      ),
    [allRoles, actorIsSystemAdmin, targetCurrentSlug],
  );

  // Initialize form state when user data loads; fall back to the legacy role
  // for users without an RBAC assignment (saving then provisions them).
  // Initialize once per open: the cached copy renders first and a background
  // refetch follows — re-initializing on every response would silently wipe
  // edits made in that window.
  const initializedForUser = useRef<number | null>(null);
  useEffect(() => {
    if (userRbac && allRoles.length > 0 && initializedForUser.current !== userRbac.userId) {
      initializedForUser.current = userRbac.userId;
      let roleId = userRbac.primaryRole?.id ?? null;
      const legacySlug = normalizeRole(legacyRole) || null;
      if (roleId === null && isPanelRoleSlug(legacySlug)) {
        roleId = allRoles.find((r) => r.slug === legacySlug)?.id ?? null;
      }
      setSelectedRoleId(roleId);
      setThreadsAdvanced(userRbac.threadsAdvancedEnabled);
      const overrideMap: ModuleOverrideState = {};
      userRbac.overrides.forEach((o) => {
        overrideMap[o.moduleKey] = o.accessLevel;
      });
      setModuleOverrides(overrideMap);
      setHasChanges(false);
    }
  }, [userRbac, legacyRole, allRoles]);

  useEffect(() => {
    if (!open) {
      initializedForUser.current = null;
      setSelectedRoleId(null);
      setThreadsAdvanced(false);
      setModuleOverrides({});
      setHasChanges(false);
      setPendingRoleId(null);
    }
  }, [open]);

  const selectedRole = useMemo(
    () => allRoles.find((r) => r.id === selectedRoleId) ?? null,
    [allRoles, selectedRoleId],
  );
  const selectedSlug = selectedRole?.slug ?? null;
  const selectedIsAdmin = isAdminRoleSlug(selectedSlug);

  // Only System Admins may upgrade or downgrade other System Admins.
  const roleSelectDisabled = targetCurrentSlug === 'SYSTEM_ADMIN' && !actorIsSystemAdmin;

  const applyRoleChange = (roleId: number) => {
    const newSlug = allRoles.find((r) => r.id === roleId)?.slug ?? null;
    setSelectedRoleId(roleId);
    // Role change resets per-user overrides and re-derives the Advanced default.
    setModuleOverrides({});
    setThreadsAdvanced(threadsAdvancedDefault(newSlug));
    setHasChanges(true);
  };

  const handleRoleChange = (roleIdStr: string) => {
    const newRoleId = parseInt(roleIdStr, 10);
    const newSlug = allRoles.find((r) => r.id === newRoleId)?.slug ?? null;
    // Warn when promoting a member role into an admin role.
    if (isAdminRoleSlug(newSlug) && !isAdminRoleSlug(selectedSlug)) {
      setPendingRoleId(newRoleId);
      return;
    }
    applyRoleChange(newRoleId);
  };

  const handleModuleOverride = (moduleKey: string, level: ModuleAccessLevel | null) => {
    setModuleOverrides((prev) => ({ ...prev, [moduleKey]: level }));
    setHasChanges(true);
  };

  const handleThreadsAdvancedChange = (checked: boolean) => {
    setThreadsAdvanced(checked);
    setHasChanges(true);
  };

  // ---------------------------------------------------------------------
  // Derived display state
  // ---------------------------------------------------------------------

  const advancedDefault = threadsAdvancedDefault(selectedSlug);
  const advancedIsOverride = threadsAdvanced !== advancedDefault;

  // Threads: role default from the DB (full for every role), override on top.
  const threadsDbDefault =
    (selectedRole?.permissions.find((p) => p.moduleKey === THREADS_MODULE_KEY)
      ?.accessLevel as ModuleAccessLevel | undefined) ?? 'full';
  const threadsOverride = moduleOverrides[THREADS_MODULE_KEY] ?? null;
  const threadsLevel = threadsOverride ?? threadsDbDefault;

  // Admin module effective levels: matrix default unless overridden (unlocked rows only).
  const adminModuleState = useMemo(
    () =>
      ADMIN_MODULES.map((mod) => {
        const def = adminModuleDefault(selectedSlug, mod.key);
        const override = def.locked ? null : (moduleOverrides[mod.key] ?? null);
        return {
          ...mod,
          locked: def.locked,
          defaultLevel: def.level,
          level: override ?? def.level,
          isOverride: override !== null && override !== def.level,
        };
      }),
    [selectedSlug, moduleOverrides],
  );

  const accessSummary = useMemo(() => {
    const full: string[] = [];
    const read: string[] = [];
    if (selectedIsAdmin) {
      adminModuleState.forEach((m) => {
        if (m.level === 'full') full.push(m.label);
        else if (m.level === 'read') read.push(m.label);
      });
    }
    if (threadsLevel === 'full') full.push('Threads');
    else if (threadsLevel === 'read') read.push('Threads');
    if (selectedIsAdmin) full.push('Finances');
    return { full, read };
  }, [selectedIsAdmin, adminModuleState, threadsLevel]);

  // ---------------------------------------------------------------------
  // Save
  // ---------------------------------------------------------------------

  const handleSave = async () => {
    if (!userId) return;

    const payload: UpdateUserRbacPayload = {};

    if (selectedRoleId !== null && selectedRoleId !== userRbac?.primaryRole?.id) {
      payload.roleId = selectedRoleId;
    }

    // The server replaces the full override list on save (clear + insert), so
    // every stored entry must be resubmitted or it is deleted. The form state
    // already carries all stored overrides (including keys this panel doesn't
    // render, e.g. crm.* revocations and T-370 member admin revocations) —
    // pass them through verbatim. Drop only entries the matrix contradicts:
    //  - keys locked for an admin-role target (matrix is authoritative; a
    //    stored 'none' there would poison effective access, and the server
    //    rejects resubmitting them),
    //  - admin-module *grants* on member-role targets (panel can't create
    //    them and the server rejects them; member 'none' revocations survive).
    const adminModuleKeys = new Set(ADMIN_MODULES.map((m) => m.key));
    const dropEntry = (key: string, level: ModuleAccessLevel): boolean => {
      if (!adminModuleKeys.has(key)) return false;
      if (selectedIsAdmin) return adminModuleDefault(selectedSlug, key).locked;
      return level !== 'none';
    };

    const overrideEntries = Object.entries(moduleOverrides)
      .filter(
        (entry): entry is [string, ModuleAccessLevel] =>
          entry[1] !== null && !dropEntry(entry[0], entry[1]),
      )
      .map(([moduleKey, accessLevel]) => ({ moduleKey, accessLevel }));

    // Send the (possibly empty) list whenever it differs from what's stored,
    // so removing the last override actually clears it server-side.
    const storedOverrides = (userRbac?.overrides ?? [])
      .map((o) => `${o.moduleKey}:${o.accessLevel}`)
      .sort()
      .join('|');
    const nextOverrides = overrideEntries
      .map((o) => `${o.moduleKey}:${o.accessLevel}`)
      .sort()
      .join('|');
    if (storedOverrides !== nextOverrides) {
      payload.moduleOverrides = overrideEntries;
    }

    if (threadsAdvanced !== userRbac?.threadsAdvancedEnabled) {
      payload.threadsAdvancedEnabled = threadsAdvanced;
    }

    if (Object.keys(payload).length === 0) {
      onOpenChange(false);
      return;
    }

    try {
      await updateMutation.mutateAsync({ userId, payload });
      toast({
        title: 'Access Updated',
        description: `Successfully updated access for ${userName}`,
      });
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      toast({
        title: 'Update Failed',
        description: error instanceof Error ? error.message : 'Failed to update user access',
        variant: 'destructive',
      });
    }
  };

  // ---------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------

  const renderLevelSelect = (
    value: ModuleAccessLevel,
    onChange: (level: ModuleAccessLevel) => void,
    opts: { disabled?: boolean; isOverride?: boolean } = {},
  ) => (
    <Select
      value={value}
      onValueChange={(v) => onChange(v as ModuleAccessLevel)}
      disabled={opts.disabled}
    >
      <SelectTrigger
        className={`w-[130px] h-8 text-xs ${opts.isOverride ? 'border-amber-500' : ''}`}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {LEVEL_OPTIONS.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            <span className="flex items-center gap-1">
              <LevelIcon level={o.value} />
              {o.label}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  const pendingRole = allRoles.find((r) => r.id === pendingRoleId) ?? null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-[560px] flex flex-col h-full">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Role & Access
          </SheetTitle>
          <SheetDescription>
            Manage permissions for <span className="font-medium">{userName}</span>
            <br />
            <span className="text-xs">{userEmail}</span>
          </SheetDescription>
        </SheetHeader>

        {isLoading ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <ScrollArea className="flex-1 pr-4 -mr-4">
            <div className="space-y-6 py-4">
              {/* Role */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Shield className="h-4 w-4" />
                    Role
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Select the user's primary role. This determines default module access.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Select
                    value={selectedRoleId?.toString() ?? ''}
                    onValueChange={handleRoleChange}
                    disabled={roleSelectDisabled}
                  >
                    <SelectTrigger data-testid="role-access-role-select">
                      <SelectValue placeholder="Select a role..." />
                    </SelectTrigger>
                    <SelectContent>
                      {panelRoles.map((role) => (
                        <SelectItem
                          key={role.id}
                          value={role.id.toString()}
                          data-testid={`role-access-option-${role.slug}`}
                        >
                          {role.displayName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedSlug && isPanelRoleSlug(selectedSlug) && (
                    <p className="text-xs text-muted-foreground">
                      {ROLE_DESCRIPTIONS[selectedSlug]}
                    </p>
                  )}
                  {roleSelectDisabled && (
                    <p className="text-xs text-amber-600">
                      Only System Admins can change another System Admin's role.
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Threads Advanced Tools */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Zap className="h-4 w-4" />
                    Threads Advanced Tools
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Allows access to LLM provider/model selection and advanced automations.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="threads-advanced" className="text-sm">
                      Enable advanced tools
                    </Label>
                    <Switch
                      id="threads-advanced"
                      data-testid="role-access-advanced-toggle"
                      checked={threadsAdvanced}
                      onCheckedChange={handleThreadsAdvancedChange}
                    />
                  </div>
                  <p
                    className={`text-xs ${advancedIsOverride ? 'text-amber-600' : 'text-muted-foreground'}`}
                  >
                    Default: ON for System Admin, OFF for all other roles.
                    {advancedIsOverride ? ' (override)' : ''}
                  </p>
                </CardContent>
              </Card>

              {/* In-App Module Access */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Settings className="h-4 w-4" />
                    In-App Module Access
                    <Badge variant="secondary" className="text-[10px]">
                      IN-APP
                    </Badge>
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Member-facing modules.
                    <span className="text-amber-600 ml-1">(Overrides shown in amber)</span>
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  {/* Threads */}
                  <div className="flex items-center justify-between py-2 px-3 rounded-md bg-muted/50">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">Threads</div>
                      <div className="text-xs text-muted-foreground">
                        AI workspace. <span className="font-medium">Default: on for all users.</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-3">
                      {threadsOverride !== null && threadsOverride !== threadsDbDefault && (
                        <Badge
                          variant="outline"
                          className="text-[10px] border-amber-500 text-amber-600"
                        >
                          OVERRIDE
                        </Badge>
                      )}
                      {renderLevelSelect(
                        threadsLevel,
                        (level) =>
                          handleModuleOverride(
                            THREADS_MODULE_KEY,
                            level === threadsDbDefault ? null : level,
                          ),
                        {
                          isOverride: threadsOverride !== null && threadsOverride !== threadsDbDefault,
                        },
                      )}
                    </div>
                  </div>

                  {/* Finances */}
                  <div className="flex items-center justify-between py-2 px-3 rounded-md bg-muted/50">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">Finances</div>
                      <div className="text-xs text-muted-foreground">
                        Invoices and commissions.
                        {selectedIsAdmin
                          ? ' Full access for admin roles.'
                          : ' Finance controls coming soon.'}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-3">
                      <Badge variant="outline" className="text-[10px] text-muted-foreground">
                        <Lock className="h-2.5 w-2.5 mr-1" />
                        LOCKED
                      </Badge>
                      {selectedIsAdmin &&
                        renderLevelSelect('full', () => undefined, { disabled: true })}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Admin Module Access */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Shield className="h-4 w-4" />
                    Admin Module Access
                    <Badge variant="secondary" className="text-[10px]">
                      ADMIN CENTER
                    </Badge>
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Admin Center sections. Only System Admins and Admins can hold these.
                    <span className="text-amber-600 ml-1">(Overrides shown in amber)</span>
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  {!selectedIsAdmin ? (
                    <div
                      className="text-xs text-muted-foreground py-3 px-3 rounded-md bg-muted/50 flex items-center gap-2"
                      data-testid="role-access-admin-lockout"
                    >
                      <Lock className="h-4 w-4 shrink-0" />
                      <span>
                        Member roles (Agency Owner, Manager, Associate Advisor) have{' '}
                        <span className="font-semibold">no access to the admin panel</span> and
                        cannot be granted admin privileges.
                      </span>
                    </div>
                  ) : (
                    adminModuleState.map((mod) => (
                      <div
                        key={mod.key}
                        className="flex items-center justify-between py-2 px-3 rounded-md bg-muted/50"
                        data-testid={`role-access-admin-${mod.key}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{mod.label}</div>
                          <div className="text-xs text-muted-foreground truncate">
                            {mod.locked
                              ? 'Auto-granted by role'
                              : mod.description}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 ml-3">
                          {mod.locked && (
                            <Badge variant="outline" className="text-[10px] text-muted-foreground">
                              <Lock className="h-2.5 w-2.5 mr-1" />
                              LOCKED
                            </Badge>
                          )}
                          {mod.isOverride && (
                            <Badge
                              variant="outline"
                              className="text-[10px] border-amber-500 text-amber-600"
                            >
                              OVERRIDE
                            </Badge>
                          )}
                          {renderLevelSelect(
                            mod.level,
                            (level) =>
                              handleModuleOverride(
                                mod.key,
                                level === mod.defaultLevel ? null : level,
                              ),
                            { disabled: mod.locked, isOverride: mod.isOverride },
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>

              {/* Access Summary */}
              <Card className="bg-muted/30">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Access Summary</CardTitle>
                </CardHeader>
                <CardContent className="text-xs space-y-2" data-testid="role-access-summary">
                  <div>
                    {accessSummary.full.length > 0 && (
                      <>
                        <span className="font-medium text-green-600">Full access:</span>{' '}
                        {accessSummary.full.join(', ')}.{' '}
                      </>
                    )}
                    {accessSummary.read.length > 0 && (
                      <>
                        <span className="font-medium text-blue-600">Read:</span>{' '}
                        {accessSummary.read.join(', ')}.
                      </>
                    )}
                    {accessSummary.full.length === 0 && accessSummary.read.length === 0 && (
                      <span className="text-muted-foreground">No module access.</span>
                    )}
                  </div>
                  <Separator className="my-2" />
                  <div>
                    <span className="font-medium">Threads Advanced:</span>{' '}
                    <Badge variant={threadsAdvanced ? 'default' : 'secondary'} className="text-xs">
                      {threadsAdvanced ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            </div>
          </ScrollArea>
        )}

        <SheetFooter className="mt-4 pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            data-testid="role-access-save"
            onClick={handleSave}
            disabled={!hasChanges || updateMutation.isPending}
          >
            {updateMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Changes
          </Button>
        </SheetFooter>

        {/* Member → admin promotion warning */}
        <AlertDialog
          open={pendingRoleId !== null}
          onOpenChange={(o) => {
            if (!o) setPendingRoleId(null);
          }}
        >
          <AlertDialogContent data-testid="role-access-promotion-warning">
            <AlertDialogHeader>
              <AlertDialogTitle>Grant admin privileges?</AlertDialogTitle>
              <AlertDialogDescription>
                You are changing <span className="font-medium">{userName}</span> from a member
                role to <span className="font-medium">{pendingRole?.displayName}</span>. This
                grants access to the Admin Center. Are you sure?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                data-testid="role-access-promotion-confirm"
                onClick={() => {
                  if (pendingRoleId !== null) applyRoleChange(pendingRoleId);
                  setPendingRoleId(null);
                }}
              >
                Change role
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </SheetContent>
    </Sheet>
  );
}
